<template>
  <div class="accordion mb-6 google-form-section">
    <b-card no-body class="mb-1">
      <b-card-header header-tag="header" role="tab">
        <b-button
          block
          size="sm"
          class="btn-collapse"
          @click.stop="toggleSection"
        >
          <div class="w-100 d-flex justify-content-between align-items-start">
            <b-row>
              <b-col
                cols="12"
                class="d-flex align-items-start justify-content-between"
              >
                <h6 class="mb-0 mr-2 font-weight-bolder section-title">
                  Phần {{ section.order }}: {{ section.name }}
                </h6>

                <span
                  class="px-4 py-2 rounded mr-8 text-white d-inline-block"
                  :class="`bg-${progressInfo.variant}`"
                >
                  {{ progressInfo.text }}
                </span>
              </b-col>
            </b-row>
            <div class="d-flex">
              <b-button
                size="sm"
                class="icon-chevron"
                @click.stop="toggleSection"
              >
                <v-icon class="p-0" size="24"
                  >mdi-{{ open ? 'chevron-up' : 'chevron-down' }}</v-icon
                >
              </b-button>
            </div>
          </div>
        </b-button>
      </b-card-header>
      <b-collapse
        :id="`accordion-${id}`"
        v-model="open"
        accordion="my-accordion"
        role="tabpanel"
      >
        <b-card-body class="google-form-body">
          <div
            v-for="(item, index) in section.questions"
            :key="item.id"
            class="question-item"
          >
            <div class="question-header">
              <div class="w-100">
                <span class="question-number">{{ index + 1 }}.</span>
                <span class="question-code">{{ item.code }}</span>
              </div>
              <div
                class="d-flex align-items-start justify-content-between w-100"
              >
                <h5 class="question-name m-0 mr-4">{{ item.name }}</h5>
                <p
                  v-if="getCreatorInfo(item)"
                  class="m-0 mr-10 border px-4 py-1 rounded question-user text-center"
                  :class="{
                    'text-success border-success': getCreatorInfo(item).isUser,
                    'text-danger border-danger': getCreatorInfo(item).isCoach,
                  }"
                >
                  {{ getCreatorInfo(item).label }}
                </p>
              </div>
            </div>
            <div class="answer-section">
              <b-form-radio-group
                v-if="
                  item.type === 1 && item.answers && item.answers.length > 0
                "
                v-model="selectedAnswers[item.id]"
                :options="getAnswerOptions(item.answers)"
                stacked
                @change="saveAnswer(item, $event)"
                class="custom-radio-group"
              />

              <b-form-checkbox-group
                v-else-if="
                  item.type === 2 && item.answers && item.answers.length > 0
                "
                v-model="selectedAnswers[item.id]"
                :options="getAnswerOptions(item.answers)"
                stacked
                @change="saveAnswer(item, $event)"
                class="custom-checkbox-group"
              />
              <b-form-textarea
                v-else-if="item.type === 3"
                v-model="selectedAnswers[item.id]"
                placeholder="Nhập câu trả lời..."
                rows="2"
                max-rows="6"
                size="sm"
                class="google-textarea"
                @blur="saveTextAnswer(item)"
              />
            </div>
            <div class="note-section">
              <b-form-textarea
                v-if="item.type !== 3 && item.result && item.result.length > 0"
                v-model="item.result[0].note"
                placeholder="Nhập ghi chú..."
                rows="4"
                max-rows="8"
                size="sm"
                class="google-note-textarea"
                @blur="saveNote(item)"
                @keydown.enter.exact.prevent
                @keyup.enter.exact="saveNote(item)"
              />
            </div>
          </div>
        </b-card-body>
      </b-collapse>
    </b-card>
  </div>
</template>

<script>
export default {
  name: 'CollapseQuestionSection',
  props: {
    patientId: {
      type: String,
      default: null,
    },
    patientInfo: {
      type: Object,
      default: () => ({}),
    },
    id: {
      type: String,
      default: null,
    },
    section: {
      type: Object,
      default: () => {},
    },
    accountId: {
      type: String,
      default: null,
    },
    ownPackage: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      open: true,
      editedByCoachQuestions: new Set(),
      selectedAnswers: {},
      headers: [
        {
          key: 'code',
          label: 'Mã câu hỏi',
          sortable: false,
          style: {
            width: '10%',
          },
        },
        {
          key: 'name',
          label: 'Câu hỏi',
          sortable: false,
          style: {
            width: '30%',
          },
        },
        {
          key: 'answer',
          label: 'Câu trả lời',
          sortable: false,
          style: {
            width: '30%',
          },
        },
        {
          key: 'note',
          label: 'Ghi chú',
          sortable: false,
          class: 'text-center',
          style: {
            width: '30%',
          },
        },
      ],
    };
  },

  computed: {
    answeredCount() {
      if (!this.section || !this.section.questions) return 0;

      return this.section.questions.filter((question) => {
        if (!question.result || question.result.length === 0) return false;

        const firstResult = question.result[0];

        if (question.type === 1) {
          return firstResult.surveyAnswerId != null;
        }

        if (question.type === 2) {
          return question.result.some((r) => r.surveyAnswerId != null);
        }

        if (question.type === 3) {
          return firstResult.content && firstResult.content.trim() !== '';
        }

        return false;
      }).length;
    },

    totalQuestions() {
      return this.section.questions ? this.section.questions.length : 0;
    },

    progressText() {
      return `${this.answeredCount}/${this.totalQuestions}`;
    },
    progressInfo() {
      const total = this.totalQuestions;
      const answered = this.answeredCount;

      if (total === 0) {
        return {
          text: '0/0',
          variant: 'secondary',
        };
      }

      if (answered === 0) {
        return {
          text: `${answered}/${total}`,
          variant: 'danger',
        };
      }

      if (answered < total) {
        return {
          text: `${answered}/${total}`,
          variant: 'warning',
        };
      }

      return {
        text: `${answered}/${total}`,
        variant: 'success',
      };
    },
  },
  mounted() {
    if (this.section && this.section.questions) {
      this.section.questions.forEach((question) => {
        if (
          question.type === 1 &&
          question.result &&
          question.result.length > 0
        ) {
          const selectedAnswerId = question.result[0].surveyAnswerId;
          if (selectedAnswerId) {
            this.$set(this.selectedAnswers, question.id, selectedAnswerId);
          }
        } else if (
          question.type === 2 &&
          question.result &&
          question.result.length > 0
        ) {
          const answerIds = question.result
            .map((r) => r.surveyAnswerId)
            .filter((id) => id);
          if (answerIds.length > 0) {
            this.$set(this.selectedAnswers, question.id, answerIds);
          } else {
            this.$set(this.selectedAnswers, question.id, []);
          }
        } else if (
          question.type === 3 &&
          question.result &&
          question.result.length > 0
        ) {
          const content = question.result[0].content;
          if (content) {
            this.$set(this.selectedAnswers, question.id, content);
          }
        } else {
          this.$set(
            this.selectedAnswers,
            question.id,
            question.type === 2 ? [] : null,
          );
        }
      });
    }
  },
  methods: {
    toggleSection() {
      if (!this.open) {
        this.open = true;

        this.$nextTick(() => {
          setTimeout(() => {
            const modalBody = document.querySelector('.modal.show .modal-body');

            if (modalBody) {
              this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });

              modalBody.scrollBy({ top: -150, behavior: 'smooth' });
            } else {
              this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              window.scrollBy(0, -150);
            }
          }, 400);
        });
      } else {
        this.open = false;
      }
    },
    getCreatorInfo(item) {
      if (this.editedByCoachQuestions.has(item.id)) {
        return { label: 'Coach', isCoach: true, isUser: false };
      }

      if (item.result && item.result.length > 0 && item.result[0].creatorId) {
        const creatorId = item.result[0].creatorId;
        const patientAccountId = this.patientInfo?.accountId;

        if (patientAccountId && creatorId === patientAccountId) {
          return { label: 'Người dùng', isUser: true, isCoach: false };
        }

        return { label: 'Coach', isCoach: true, isUser: false };
      }

      return null;
    },
    getAnswerName(result) {
      if (result && result.length) {
        return result.map((el) => el.answerName || el.content);
      }
      return [];
    },
    getNoteAndPoint(result) {
      if (!result || result.length === 0) {
        return null;
      }

      const firstResult = result[0];
      let display = [];

      if (
        firstResult.note !== null &&
        firstResult.note !== undefined &&
        firstResult.note.trim() !== ''
      ) {
        display.push(firstResult.note.trim());
      }

      if (firstResult.point !== null && firstResult.point !== undefined) {
        display.push(`(${firstResult.point} điểm)`);
      }

      return display.length > 0 ? display.join(' ') : null;
    },
    getAnswerOptions(answers) {
      return answers.map((answer) => ({
        value: answer.id,
        text: answer.content,
      }));
    },

    async saveAnswer(item, answerIds) {
      this.loading = true;

      let surveyAnswerIdList = [];
      if (item.type === 1 && answerIds) {
        surveyAnswerIdList = [answerIds];
      } else if (
        item.type === 2 &&
        Array.isArray(answerIds) &&
        answerIds.length > 0
      ) {
        surveyAnswerIdList = answerIds;
      }

      const noteValue =
        item.result && item.result.length > 0 ? item.result[0].note : null;

      try {
        const payload = {
          AccountId: this.patientInfo.accountId,
          SurveyQuestionId: item.id,
          SurveySectionId: this.section.id,
          Note: noteValue,
          PackageAccountTransactionId:
            this.patientInfo.packageAccountTransactionId || null,
          ...(surveyAnswerIdList.length > 0 && {
            SurveyAnswerIdList: surveyAnswerIdList,
          }),
        };

        await this.$api.put('/SurveyResult', payload);

        if (surveyAnswerIdList.length > 0) {
          if (item.type === 1) {
            const selectedAnswer = item.answers.find(
              (a) => a.id === surveyAnswerIdList[0],
            );
            item.result = [
              {
                surveyAnswerId: surveyAnswerIdList[0],
                answerName: selectedAnswer?.content || null,
                point: selectedAnswer?.point || 0,
                content: null,
                note: noteValue,
              },
            ];
          } else if (item.type === 2) {
            item.result = surveyAnswerIdList.map((answerId) => {
              const selectedAnswer = item.answers.find(
                (a) => a.id === answerId,
              );
              return {
                surveyAnswerId: answerId,
                answerName: selectedAnswer?.content || null,
                point: selectedAnswer?.point || 0,
                content: null,
                note: noteValue,
              };
            });
          }
        } else {
          item.result = [{ note: noteValue }];
        }
        this.editedByCoachQuestions.add(item.id);
        this.$root.$emit('onShowForm-event');
      } catch (err) {
        this.$toastr.e({
          title: 'Lỗi!',
          msg: 'Đã có lỗi xảy ra',
        });
      } finally {
        this.loading = false;
      }
    },

    async saveTextAnswer(item) {
      this.loading = true;

      const content = this.selectedAnswers[item.id] || null;
      const noteValue =
        item.result && item.result.length > 0 ? item.result[0].note : null;

      try {
        const payload = {
          AccountId: this.patientInfo.accountId,
          SurveyQuestionId: item.id,
          SurveySectionId: this.section.id,
          Content: content,
          Note: noteValue,
          PackageAccountTransactionId:
            this.patientInfo.packageAccountTransactionId || null,
        };

        await this.$api.put('/SurveyResult', payload);

        if (content) {
          item.result = [{ content, note: noteValue }];
        } else {
          item.result = [{ note: noteValue }];
        }
        this.editedByCoachQuestions.add(item.id);
        this.$root.$emit('onShowForm-event');
      } catch (err) {
        this.$toastr.e({
          title: 'Lỗi!',
          msg: 'Đã có lỗi xảy ra',
        });
      } finally {
        this.loading = false;
      }
    },

    async saveNote(item) {
      this.loading = true;

      try {
        const noteValue =
          item.result && item.result.length > 0 ? item.result[0].note : null;

        let payload = {
          AccountId: this.patientInfo.accountId,
          SurveyQuestionId: item.id,
          SurveySectionId: this.section.id,
          Note: noteValue,
          PackageAccountTransactionId:
            this.patientInfo.packageAccountTransactionId || null,
        };

        if (item.type === 1 || item.type === 2) {
          const existingIds = item.result
            ? item.result.map((r) => r.surveyAnswerId).filter((id) => id)
            : [];
          if (existingIds.length > 0) {
            payload.SurveyAnswerIdList = existingIds;
          }
        }

        if (item.type === 3) {
          const existingContent =
            item.result && item.result.length > 0 && item.result[0].content;
          if (existingContent) {
            payload.Content = existingContent;
          }
        }

        await this.$api.put('/SurveyResult', payload);
        if (
          (item.type !== 3 &&
            item.result &&
            item.result.some((r) => r.surveyAnswerId != null)) ||
          (item.type === 3 && item.result[0].content?.trim())
        ) {
          this.editedByCoachQuestions.add(item.id);
        }
        this.$root.$emit('onShowForm-event');
      } catch (err) {
        this.$toastr.e({
          title: 'Lỗi!',
          msg: 'Đã có lỗi xảy ra',
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.google-form-section {
  font-family: 'Roboto', sans-serif;
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border: 1px solid #dadce0;
  border-radius: 8px;
  padding: 16px;
}

.section-title {
  color: #01645a;
  font-size: 1.825rem;
  margin-bottom: 16px;
}

.google-form-body {
  padding: 24px;
}

.question-item {
  margin-bottom: 32px;
  border-bottom: 1px solid #dadce0;
  padding-bottom: 16px;
}

.question-header {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.question-code {
  font-size: 0.9rem;
  color: #5f6368;
  margin-right: 8px;
  font-weight: bold;
}

.question-name {
  font-size: 1.5rem;
  font-weight: 500;
  width: 100%;
  color: #202124;
}

.question-user {
  min-width: 125px;
}

.answer-section {
  margin-bottom: 16px;
}

.google-radio-group,
.google-checkbox-group {
  .custom-control-label {
    color: #202124;
  }
}

::v-deep .custom-control {
  margin-bottom: 8px;
}

.google-textarea,
.google-note-textarea {
  border: none;
  border-bottom: 1px solid #dadce0;
  border-radius: 0;
  resize: none;
  font-size: 1.25rem;
  color: #202124;
  &:focus {
    border-bottom: 2px solid #01645a;
    box-shadow: none;
  }
}

.note-section {
  margin-top: 16px;
}

.btn-collapse {
  background-color: white !important;
  border: none !important;
  text-align: left;
  padding: 16px;
  &:hover {
    background: #f8f9fa !important;
  }
}
.custom-radio-group::v-deep
  .custom-control-input:checked
  ~ .custom-control-label::before {
  background-color: #07bf9b !important;
  border-color: #07bf9b !important;
  box-shadow: 0 0 0 0.2rem rgba(7, 191, 155, 0.25);
}

.custom-radio-group::v-deep
  .custom-control-input:focus
  ~ .custom-control-label::before {
  box-shadow: 0 0 0 0.2rem rgba(7, 191, 155, 0.25);
}

.custom-radio-group::v-deep .custom-control-label::before {
  border-radius: 50%;
  border: 2px solid #ced4da;
}

.custom-checkbox-group::v-deep
  .custom-control-input:checked
  ~ .custom-control-label::before {
  background-color: #07bf9b !important;
  border-color: #07bf9b !important;
  box-shadow: 0 0 0 0.2rem rgba(7, 191, 155, 0.25);
}

.custom-checkbox-group::v-deep
  .custom-control-input:focus
  ~ .custom-control-label::before {
  box-shadow: 0 0 0 0.2rem rgba(7, 191, 155, 0.25);
}

.custom-checkbox-group::v-deep .custom-control-label::before {
  border-radius: 4px;
  border: 2px solid #ced4da;
}

.custom-radio-group::v-deep .custom-control-label:hover::before,
.custom-checkbox-group::v-deep .custom-control-label:hover::before {
  border-color: #07bf9b;
}

.custom-radio-group::v-deep .custom-control-label,
.custom-checkbox-group::v-deep .custom-control-label {
  font-size: 1.25rem !important;
  color: #202124;
  line-height: 1.8;
  padding-left: 12px;
}
.custom-radio-group::v-deep .custom-control-label::before,
.custom-radio-group::v-deep .custom-control-label::after,
.custom-checkbox-group::v-deep .custom-control-label::before,
.custom-checkbox-group::v-deep .custom-control-label::after {
  width: 1.25rem !important;
  height: 1.25rem !important;
  background-size: 60% 60%;
}

.custom-radio-group::v-deep .custom-control-label,
.custom-checkbox-group::v-deep .custom-control-label {
  padding-left: 0.5rem;
}

.custom-radio-group::v-deep
  .custom-control-input:checked
  ~ .custom-control-label::after {
  background-size: 70% 70% !important;
}

.custom-checkbox-group::v-deep
  .custom-control-input:checked
  ~ .custom-control-label::after {
  background-size: 70% 70% !important;
}
</style>
